import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SiteContext';
import SiteSelector from './SiteSelector';
import './Header.css';

const Header = () => {
  const { user, logout, hasRole, loading } = useAuth();
  const { sites } = useSubdomain();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // No mostrar header cuando no hay usuario autenticado
  if (!user) {
    return null;
  }

  const handleLogout = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      console.log('Iniciando logout...');
      setShowUserMenu(false); // Cerrar el menú
      await logout();
      console.log('Logout completado');
    } catch (error) {
      console.error('Error en logout:', error);
    }
  };

  const toggleUserMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(!showUserMenu);
  };

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-left">
          <a href="/" className="app-title-link">
            <h1 className="app-title">Olimpo</h1>
          </a>
        </div>

        {/* Site Selector para managers */}
        {!loading && user && hasRole('manager') && sites.length > 0 && (
          <div className="header-center">
            <SiteSelector compact={true} showLabel={false} />
          </div>
        )}

        <div className="header-right">
          {user ? (
            <div className="user-menu" ref={userMenuRef}>
              <button 
                className="user-button"
                onClick={toggleUserMenu}
              >
                <div className="user-avatar">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className="user-name">{user.name || user.email}</span>
                <svg className="dropdown-arrow" viewBox="0 0 24 24">
                  <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                </svg>
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <div className="user-details">
                      <div className="user-name">{user.name}</div>
                      <div className="user-email">{user.email}</div>
                      <div className="user-roles">
                        {user.roles && user.roles.map(role => (
                          <span key={role} className="role-badge">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  
                  <div className="dropdown-menu">
                    <a href="/profile" className="dropdown-item">
                      <svg className="menu-icon" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                      </svg>
                      Mi Perfil
                    </a>
                    
                    <a href="/settings" className="dropdown-item">
                      <svg className="menu-icon" viewBox="0 0 24 24">
                        <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" fill="currentColor"/>
                      </svg>
                      Configuración
                    </a>
                    
                    {hasRole('admin') && (
                      <>
                        <div className="dropdown-divider"></div>
                        <a href="/administration" className="dropdown-item admin-item">
                          <svg className="menu-icon" viewBox="0 0 24 24">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                          </svg>
                          Administración
                        </a>
                      </>
                    )}
                    
                    <button onClick={handleLogout} className="dropdown-item logout-item">
                      <svg className="menu-icon" viewBox="0 0 24 24">
                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
                      </svg>
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <a href="/login" className="login-link">
              Iniciar Sesión
            </a>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
